import Field from 'components/Form/Field';
import {FormContextString, InputProps} from 'components/Form/types';
import {useTooltip} from 'components/Tooltip/hook';
import useFormError from 'hooks/useFormErrors';
import {FC, useState} from 'react';
import {useFormContext} from 'react-hook-form';
import {twMerge} from 'tailwind-merge';
import {t} from 'ttag';

import PasswordRules, {checkPasswordConditions} from './PasswordRules';

export type InputPasswordProps = {
    classNameButton?: string;
    classNameChain?: string;
    hasValidation?: boolean;
    showPasswordRules?: boolean;
} & InputProps;

const InputPassword: FC<InputPasswordProps> = ({
    className,
    classNameChain,
    classNameInput,
    disabled,
    error,
    hasValidation = true,
    helpText,
    id,
    label,
    name,
    readOnly,
    required,
    requiredType,
    rules,
    showPasswordRules,
    ...rest
}) => {
    const {setTooltipRef} = useTooltip();

    const {getValues, register} = useFormContext<FormContextString>();

    const [passwordValue, setPasswordValue] = useState(
        () => getValues(name) || '',
    );

    const [type] = useState<'password'>('password');

    const registerRules = rules || {
        disabled,
        required,
        validate: hasValidation
            ? (value) => checkPasswordConditions(value).valid
            : undefined,
    };

    const fieldError = useFormError(name, error);

    const showErrors = !!fieldError;

    const fieldLabel =
        label || (showPasswordRules ? t`Password` : t`Confirm Password`);

    const {ref, ...registration} = register(name, {
        ...registerRules,
        onChange: (event) => setPasswordValue(event.target.value),
    });

    return (
        <Field
            className={className}
            disabled={registerRules.disabled || readOnly}
            error={fieldError}
            helpText={helpText}
            label={fieldLabel}
            name={name}
            required={!!registerRules.required}
            requiredType={requiredType}
            type={showPasswordRules ? 'password' : 'input'}
        >
            <div
                className={twMerge(
                    'input-wrapper flex h-10 items-end',
                    showErrors && 'input-invalid',
                    disabled && 'disabled',
                    classNameChain,
                )}
                ref={(r) => {
                    setTooltipRef(r);
                }}
            >
                <input
                    aria-label={
                        rest['aria-label'] || label === null
                            ? undefined
                            : typeof label === 'string'
                              ? label
                              : name
                    }
                    className={twMerge('border-none', classNameInput)}
                    id={id || name}
                    readOnly={readOnly}
                    ref={(r) => {
                        ref(r);
                    }}
                    type={type}
                    {...registration}
                    tabIndex={readOnly ? -1 : undefined}
                    {...rest}
                />
            </div>
            {showPasswordRules && (
                <PasswordRules showErrors={showErrors} value={passwordValue} />
            )}
        </Field>
    );
};

export default InputPassword;
